import React, { useId, useState } from 'react';
import classnames from 'classnames';
import { Link } from 'react-router';
import { useDeps } from 'src/hooks/useDeps';

import { Breakpoint, useBreakpoint } from '../../hooks/useBreakpoint';
import { Svg } from '../Svg/Svg';
import { stripDomain } from '../../util/sanitizeLink';

import classes from './FooterMenus.scss';

export interface FooterMenu {
    title: string;
    children: {
        title: string;
        url: string;
    }[];
}

// Matches the `media-query(max, $palm-start)` breakpoint that collapses the lists in FooterMenus.scss.
const ACCORDION_MEDIA_QUERY = '(max-width: 599px)';

type FooterMenusProps = {
    menus: FooterMenu[];
    theme: 'white' | 'black';
};

export const FooterMenus: React.FC<FooterMenusProps> = React.memo(({ menus, theme }) => {
    const { DOMAIN } = useDeps().env;
    const [breakpoint] = useBreakpoint(ACCORDION_MEDIA_QUERY, 'FooterMenus');
    const isAccordion = breakpoint === Breakpoint.Mob;
    const [openIndexes, toggleList] = useState([] as number[]);
    const idPrefix = useId();

    return (
        <div
            className={classnames(classes.footerMenus, {
                [classes.black]: theme === 'black',
                [classes.white]: theme === 'white',
            })}
        >
            {menus.map((menu, index) => {
                const active = openIndexes.indexOf(index) > -1;
                const listId = `${idPrefix}-list-${index}`;
                return (
                    <div key={menu.title} className={classes.footerMenusMenu}>
                        <h3
                            className={classnames(classes.footerMenusTitle, {
                                [classes.footerMenusTitleActive]: active,
                                [classes.footerMenusTitleAccordion]: isAccordion,
                                [classes.black]: theme === 'black',
                                [classes.white]: theme === 'white',
                            })}
                        >
                            {isAccordion ? (
                                <button
                                    type="button"
                                    className={classes.footerMenusToggle}
                                    aria-expanded={active}
                                    aria-controls={listId}
                                    onClick={() =>
                                        toggleList((children) => {
                                            if (children.indexOf(index) > -1) {
                                                return children.filter((x) => x !== index);
                                            }
                                            return children.concat(index);
                                        })
                                    }
                                >
                                    {menu.title} <Svg name="arrow-down" />
                                </button>
                            ) : (
                                menu.title
                            )}
                        </h3>
                        <ul
                            id={listId}
                            className={classnames(classes.footerMenusList, {
                                [classes.footerMenusListActive]: active,
                            })}
                        >
                            {menu.children.map((subchildren) => (
                                <li key={subchildren.title} className={classes.footerListsItem}>
                                    <FooterMenuLink
                                        url={subchildren.url}
                                        title={subchildren.title}
                                        domain={DOMAIN}
                                        theme={theme}
                                    />
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            })}
        </div>
    );
});

type FooterMenuLinkProps = {
    url: string;
    title: string;
    domain: string;
    theme: 'white' | 'black';
};

const FooterMenuLink: React.FC<FooterMenuLinkProps> = ({ url, title, domain, theme }) => {
    if (!url) return null;

    // has domain eg workwear and does not lead to media
    if (url.includes(domain) && !url.includes('/media/')) {
        return (
            <Link
                to={stripDomain(url)}
                className={classnames(classes.footerListsLink, {
                    [classes.black]: theme === 'black',
                    [classes.white]: theme === 'white',
                })}
            >
                {title}
            </Link>
        );
    }

    // is External
    if (url.includes('https://') || url.includes('http://')) {
        return (
            <a
                href={url}
                className={classnames(classes.footerListsLink, {
                    [classes.black]: theme === 'black',
                    [classes.white]: theme === 'white',
                })}
                target="_blank"
                rel="noreferrer"
            >
                {title}
            </a>
        );
    }

    // has no domain
    return (
        <Link
            to={url}
            className={classnames(classes.footerListsLink, {
                [classes.black]: theme === 'black',
                [classes.white]: theme === 'white',
            })}
        >
            {title}
        </Link>
    );
};
